import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Card,
  SectionHeader,
  Input,
  Button,
  Icon,
  CustomSelect,
  Checkbox,
  Modal,
  Toast,
  PaginationBar,
  type ToastVariant,
} from "../components/ui";
import { useStaffSettingsState } from "../hooks/useStaffSettingsState";
import AdministratorGovernanceSection from "./users/AdministratorGovernanceSection";
import StaffCodesSection from "./users/StaffCodesSection";
import ApprovedDomainsSection from "./users/ApprovedDomainsSection";
import PendingStaffRequestsSection from "./users/PendingStaffRequestsSection";
import {
  useDeactivateStaffUserMutation,
  useDeleteStaffUserMutation,
  useInviteStaffUserMutation,
  useListAdmissionsApplicationsQuery,
  useListFinancialAidApplicationsForReviewQuery,
  useListStaffUsersQuery,
  useReactivateStaffUserMutation,
  useResendStaffInviteMutation,
  useUpdateStaffUserRoleMutation,
} from "../../../state/domainRouterApi";
import { useShellProfile } from "../../../state/useShellProfile";
import {
  buildPrincipalOptionsForDivision,
  isPreservedPrincipalSelection,
  resolveInitialPrincipalSelection,
} from "../utils/onboardingSettings";

type StaffRoleLabel =
  | "Super Administrator"
  | "Admin"
  | "Principal"
  | "Teacher"
  | "Nurse"
  | "Accounts";
type StaffStatusLabel = "Active" | "Invited" | "Deactivated";

type UserRow = {
  rowId: string;
  id: string;
  name: string;
  email: string;
  role: StaffRoleLabel;
  status: StaffStatusLabel;
  active: string;
  badgeColor: string;
};

type RowActionMenuPosition = {
  top: number;
  left: number;
};

type PermissionMatrix = Record<string, Record<StaffRoleLabel, boolean>>;

const ROLE_OPTIONS: StaffRoleLabel[] = [
  "Super Administrator",
  "Admin",
  "Principal",
  "Teacher",
  "Nurse",
  "Accounts",
];

const ROLE_TO_CANONICAL: Record<
  StaffRoleLabel,
  | "super-administrator"
  | "admin"
  | "principal"
  | "teacher"
  | "nurse"
  | "accounts"
> = {
  "Super Administrator": "super-administrator",
  Admin: "admin",
  Principal: "principal",
  Teacher: "teacher",
  Nurse: "nurse",
  Accounts: "accounts",
};

const STAFF_INVITE_EMAIL_NOT_QUEUED_MESSAGE =
  "Staff account was created, but the invitation email was not queued. Please resend the invite after email delivery is available.";
const STAFF_INVITE_RESEND_EMAIL_NOT_QUEUED_MESSAGE =
  "Invitation email was not queued. Please try again after email delivery is available.";

const createDefaultInviteForm = () => ({
  name: "",
  email: "",
  role: "Admin" as StaffRoleLabel,
});

const CANONICAL_TO_ROLE: Record<string, StaffRoleLabel> = {
  "super-administrator": "Super Administrator",
  admin: "Admin",
  principal: "Principal",
  teacher: "Teacher",
  nurse: "Nurse",
  accounts: "Accounts",
};

const STATUS_TO_CANONICAL: Record<
  StaffStatusLabel,
  "active" | "invited" | "deactivated"
> = {
  Active: "active",
  Invited: "invited",
  Deactivated: "deactivated",
};

const CANONICAL_TO_STATUS: Record<string, StaffStatusLabel> = {
  active: "Active",
  invited: "Invited",
  deactivated: "Deactivated",
};

const splitName = (value: string): { firstName: string; lastName: string } => {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return { firstName: "", lastName: "" };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const toText = (value: unknown): string => String(value ?? "").trim();

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const PAGE_SIZE = 20;
const SHOW_ADMINISTRATOR_GOVERNANCE = false;
const ROW_ACTION_MENU_WIDTH = 128;
const ROW_ACTION_MENU_ESTIMATED_HEIGHT = 132;
const ROW_ACTION_MENU_VIEWPORT_PADDING = 12;
const ROW_ACTION_MENU_LAYER_Z_INDEX = 1390;
const DELETE_WITHOUT_ASSIGNING_VALUE = "__delete_without_assigning__";

// 18 permissions across 6 section groups.
// Section boundaries (by index): Applications 0-3, Enrollment 4-5, Data Access 6-8,
// Financial 9-11, Reports 12-14, Administration 15-17.
const INITIAL_PERMISSION_ROWS = [
  // Applications (4)
  "View all applications",
  "Review applications",
  "Change app status",
  "Send acceptance letters",
  // Enrollment (2)
  "Monitor enrollment",
  "Manage enrollment",
  // Data Access (3)
  "View family contact",
  "View medical info",
  "Export student data",
  // Financial (3)
  "Record payments",
  "Issue refunds",
  "Manage financial aid",
  // Reports (3)
  "Export reports",
  "View analytics",
  "View audit logs",
  // Administration (3)
  "Manage settings",
  "Manage divisions",
  "Bulk actions",
] as const;

const PERMISSION_SECTION_STARTS: Record<number, string> = {
  0: "Applications",
  4: "Enrollment",
  6: "Data Access",
  9: "Financial",
  12: "Reports",
  15: "Administration",
};

const permissionKeyMap: Record<string, string> = {
  "View all applications": "app_view",
  "Review applications": "app_review",
  "Change app status": "app_status",
  "Send acceptance letters": "enroll_send",
  "Monitor enrollment": "enroll_monitor",
  "Manage enrollment": "enroll_manage",
  "View family contact": "data_contact",
  "View medical info": "data_medical",
  "Export student data": "data_export",
  "Record payments": "fin_record",
  "Issue refunds": "fin_refund",
  "Manage financial aid": "fin_aid",
  "Export reports": "report_export",
  "View analytics": "report_analytics",
  "View audit logs": "report_audit",
  "Manage settings": "admin_settings",
  "Manage divisions": "admin_divisions",
  "Bulk actions": "admin_bulk",
};

const buildUsers = (staff: unknown): UserRow[] => {
  if (!Array.isArray(staff)) return [];
  return staff.map((entry, index) => {
    const record = asRecord(entry);
    const role = CANONICAL_TO_ROLE[toText(record.role)] ?? "Admin";
    const status = CANONICAL_TO_STATUS[toText(record.status)] ?? "Invited";
    const firstName = toText(record.firstName);
    const lastName = toText(record.lastName);
    return {
      rowId: String(index + 1),
      id: toText(record.id) || `staff-${index + 1}`,
      name: `${firstName} ${lastName}`.trim() || "Unknown User",
      email: toText(record.email),
      role,
      status,
      active:
        toText(record.lastActive) ||
        (status === "Active" ? "Recently active" : "-"),
      badgeColor: "bg-muted text-irongrey",
    };
  });
};

const buildPermissions = (permissions: unknown): PermissionMatrix => {
  const matrix = {} as PermissionMatrix;
  INITIAL_PERMISSION_ROWS.forEach((row) => {
    matrix[row] = {
      "Super Administrator": false,
      Admin: false,
      Principal: false,
      Teacher: false,
      Nurse: false,
      Accounts: false,
    };
  });

  const source = asRecord(permissions);
  ROLE_OPTIONS.forEach((roleLabel) => {
    const roleKey = ROLE_TO_CANONICAL[roleLabel];
    const rolePermissions = asRecord(source[roleKey]);
    Object.entries(permissionKeyMap).forEach(([label, key]) => {
      matrix[label][roleLabel] = Boolean(rolePermissions[key]);
    });
  });
  return matrix;
};

// Debounce hook — gates the expensive filter memoization to avoid typing lag.
function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const getRowActionMenuPosition = (
  button: HTMLButtonElement,
): RowActionMenuPosition => {
  const rect = button.getBoundingClientRect();
  const minLeft = ROW_ACTION_MENU_WIDTH + ROW_ACTION_MENU_VIEWPORT_PADDING;
  const maxLeft = Math.max(
    minLeft,
    window.innerWidth - ROW_ACTION_MENU_VIEWPORT_PADDING,
  );
  const maxTop = Math.max(
    ROW_ACTION_MENU_VIEWPORT_PADDING,
    window.innerHeight -
      ROW_ACTION_MENU_ESTIMATED_HEIGHT -
      ROW_ACTION_MENU_VIEWPORT_PADDING,
  );

  return {
    top: Math.max(
      ROW_ACTION_MENU_VIEWPORT_PADDING,
      Math.min(rect.bottom + 8, maxTop),
    ),
    left: Math.min(Math.max(rect.right, minLeft), maxLeft),
  };
};

// onViewAs: stub prop — shell banner is shell-team scope.
// data-testid="settings-users-view-as" wraps the section wrapper in the card row above.
const noop = () => {};

export default function Users({
  onViewAs = noop,
}: {
  onViewAs?: (userId: string) => void;
}) {
  const { settingsState, persistPatch } = useStaffSettingsState();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "invited" | "deactivated"
  >("all");
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { effectivePersona } = useShellProfile();
  const isRoleEditor = effectivePersona === "super-administrator";
  const staffDirectoryQuery = useListStaffUsersQuery({
    search: debouncedSearchTerm || undefined,
    statusFilter,
    page: currentPage,
    limit: PAGE_SIZE,
  });
  const [inviteStaffUser] = useInviteStaffUserMutation();
  const [resendStaffInvite] = useResendStaffInviteMutation();
  const [updateStaffUserRole] = useUpdateStaffUserRoleMutation();
  const [deactivateStaffUser] = useDeactivateStaffUserMutation();
  const [deleteStaffUser, deleteStaffUserState] = useDeleteStaffUserMutation();
  const [reactivateStaffUser] = useReactivateStaffUserMutation();
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [activeMenuPosition, setActiveMenuPosition] =
    useState<RowActionMenuPosition | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isInviteSubmitting, setIsInviteSubmitting] = useState(false);
  const inviteSubmitInFlightRef = useRef(false);
  const [isInviteRetrySubmitting, setIsInviteRetrySubmitting] = useState(false);
  const inviteRetryInFlightRef = useRef(false);
  const [inviteRetryTarget, setInviteRetryTarget] = useState<{
    userId: string;
  } | null>(null);
  const [inviteDeliveryMessage, setInviteDeliveryMessage] = useState("");
  const [resendingInviteUserId, setResendingInviteUserId] = useState<
    string | null
  >(null);
  const [inviteForm, setInviteForm] = useState(createDefaultInviteForm);
  const [sortConfig, setSortConfig] = useState<{
    key: "name";
    direction: "asc" | "desc";
  } | null>(null);
  const [permissions, setPermissions] = useState<PermissionMatrix>(() =>
    buildPermissions({}),
  );
  const [divisionPrincipal, setDivisionPrincipal] = useState<
    Record<string, string>
  >({});
  const [divisionSecretaries, setDivisionSecretaries] = useState<
    Record<string, string>
  >({});
  const [toastMessage, setToastMessage] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const [toastVariant, setToastVariant] = useState<ToastVariant>("error");
  const [deleteTargetUser, setDeleteTargetUser] = useState<UserRow | null>(
    null,
  );
  const [reassignTargetUserId, setReassignTargetUserId] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");

  useEffect(() => {
    const serverStaff = Array.isArray(staffDirectoryQuery.data?.items)
      ? staffDirectoryQuery.data?.items.map((item, index) => ({
          rowId: String(index + 1 + (currentPage - 1) * PAGE_SIZE),
          id: item.userId,
          name: `${item.firstName} ${item.lastName}`.trim() || "Unknown User",
          email: item.email,
          role: CANONICAL_TO_ROLE[item.role] ?? "Admin",
          status: CANONICAL_TO_STATUS[item.status] ?? "Invited",
          active: item.lastActiveAt || "-",
          badgeColor: "bg-muted text-irongrey",
        }))
      : buildUsers(settingsState.staff);
    setUsers(serverStaff);
    setPermissions(buildPermissions(settingsState.permissions));
    const nextPrincipal: Record<string, string> = {};
    const nextSecretaries: Record<string, string> = {};
    settingsState.divisions.forEach((division) => {
      nextPrincipal[division.id] = resolveInitialPrincipalSelection(division);
      nextSecretaries[division.id] = division.secretaryEmail || "";
    });
    setDivisionPrincipal(nextPrincipal);
    setDivisionSecretaries(nextSecretaries);
  }, [currentPage, settingsState, staffDirectoryQuery.data]);

  const persistPermissions = async (nextPermissions: PermissionMatrix) => {
    await persistPatch((current) => {
      const canonicalPermissions: Record<string, Record<string, boolean>> = {};
      ROLE_OPTIONS.forEach((role) => {
        const roleKey = ROLE_TO_CANONICAL[role];
        canonicalPermissions[roleKey] = {};
        Object.entries(permissionKeyMap).forEach(([label, permissionKey]) => {
          canonicalPermissions[roleKey][permissionKey] = Boolean(
            nextPermissions[label]?.[role],
          );
        });
      });
      current.permissions = canonicalPermissions as typeof current.permissions;
      return current;
    });
  };

  const persistDivisionAssignments = async (
    nextPrincipal: Record<string, string>,
    nextSecretaries: Record<string, string>,
  ) => {
    await persistPatch((current) => {
      current.divisions = current.divisions.map((division) => ({
        ...division,
        principalId: isPreservedPrincipalSelection(nextPrincipal[division.id])
          ? undefined
          : nextPrincipal[division.id] || undefined,
        secretaryEmail: toText(nextSecretaries[division.id]) || undefined,
      }));
      return current;
    });
  };

  const showToast = (message: string, variant: ToastVariant = "error") => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastVisible(true);
  };

  const readErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    if (error && typeof error === "object" && "message" in error) {
      return String((error as { message?: unknown }).message ?? "");
    }
    return String(error ?? "");
  };

  const mapPersistErrorMessage = (
    error: unknown,
    context: "permissions" | "staff" | "division",
  ): string => {
    const message = readErrorMessage(error);
    const forbidden = /forbidden|requires admin|unauthorized/i.test(message);
    if (forbidden) {
      if (context === "permissions") {
        return "Permission denied. Only administrators can modify the permissions matrix.";
      }
      return "Permission denied. You do not have access to modify these settings.";
    }
    return "Failed to save changes. Please try again.";
  };

  const mapDeleteStaffUserErrorMessage = (error: unknown): string => {
    const message = readErrorMessage(error).trim();
    if (/unsupported field deleteStaffUser/i.test(message)) {
      return "Staff deletion is not available in the connected backend yet. Deploy or start the latest Amplify sandbox, then try again.";
    }
    return message && message !== "[object Object]"
      ? message
      : mapPersistErrorMessage(error, "staff");
  };

  const deleteTargetUserId = deleteTargetUser?.id ?? "";
  const admissionsAssignmentQuery = useListAdmissionsApplicationsQuery(
    deleteTargetUser
      ? { filter: { assignedTo: deleteTargetUserId }, limit: 1 }
      : undefined,
    { skip: !deleteTargetUser },
  );
  const financialAidAssignmentQuery =
    useListFinancialAidApplicationsForReviewQuery(
      deleteTargetUser
        ? { assignedTo: deleteTargetUserId, limit: 1 }
        : undefined,
      { skip: !deleteTargetUser },
    );
  const reassignmentStaffQuery = useListStaffUsersQuery(
    deleteTargetUser
      ? { statusFilter: "active", page: 1, limit: 500 }
      : undefined,
    { skip: !deleteTargetUser },
  );
  const deleteAssignedAdmissionsEnrollmentCount = Number(
    admissionsAssignmentQuery.data?.totalCount ?? 0,
  );
  const deleteAssignedFinancialAidCount = Number(
    financialAidAssignmentQuery.data?.totalCount ?? 0,
  );
  const deleteAssignedTaskCount = 0;
  const deleteAssignedWorkCount =
    deleteAssignedAdmissionsEnrollmentCount +
    deleteAssignedFinancialAidCount +
    deleteAssignedTaskCount;
  const deleteDependencyLoading =
    Boolean(deleteTargetUser) &&
    (admissionsAssignmentQuery.isFetching ||
      financialAidAssignmentQuery.isFetching ||
      reassignmentStaffQuery.isFetching);
  const deleteDependencyError = Boolean(
    admissionsAssignmentQuery.isError || financialAidAssignmentQuery.isError,
  );
  const deleteRequiresReassignment = deleteAssignedWorkCount > 0;
  const showDeleteReassignmentField =
    deleteRequiresReassignment || deleteDependencyError;
  const reassignmentOptions = useMemo(() => {
    const activeStaff = Array.isArray(reassignmentStaffQuery.data?.items)
      ? reassignmentStaffQuery.data.items.map((item) => ({
          id: item.userId,
          name: `${item.firstName} ${item.lastName}`.trim() || item.email,
          role: CANONICAL_TO_ROLE[item.role] ?? "Admin",
        }))
      : users
          .filter((user) => user.status === "Active")
          .map((user) => ({ id: user.id, name: user.name, role: user.role }));

    return [
      {
        label: "Delete without assigning",
        value: DELETE_WITHOUT_ASSIGNING_VALUE,
      },
      { label: "Select staff member", value: "" },
      ...activeStaff
        .filter((user) => user.id !== deleteTargetUserId)
        .map((user) => ({
          label: `${user.name} (${user.role})`,
          value: user.id,
        })),
    ];
  }, [deleteTargetUserId, reassignmentStaffQuery.data?.items, users]);

  const togglePermission = (row: string, column: StaffRoleLabel) => {
    const nextPermissions: PermissionMatrix = {
      ...permissions,
      [row]: {
        ...permissions[row],
        [column]: !permissions[row]?.[column],
      },
    };
    setPermissions(nextPermissions);
    void (async () => {
      try {
        await persistPermissions(nextPermissions);
      } catch (error: unknown) {
        showToast(mapPersistErrorMessage(error, "permissions"), "error");
      }
    })();
  };

  const toggleSort = () => {
    setSortConfig((previous) => {
      if (!previous || previous.direction === "desc")
        return { key: "name", direction: "asc" };
      return { key: "name", direction: "desc" };
    });
  };

  const sortedAndFilteredUsers = useMemo(
    () =>
      [...users]
        .filter((user) => {
          const matchesRole = roleFilter === "all" || user.role === roleFilter;
          return matchesRole;
        })
        .sort((left, right) => {
          if (!sortConfig) return 0;
          const leftName = left.name.toLowerCase();
          const rightName = right.name.toLowerCase();
          return sortConfig.direction === "asc"
            ? leftName.localeCompare(rightName)
            : rightName.localeCompare(leftName);
        }),
    [roleFilter, sortConfig, users],
  );

  // Page-reset whenever filters change — keep user on a valid page.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, roleFilter, statusFilter]);

  const totalFilteredPages = Math.max(
    1,
    Number(staffDirectoryQuery.data?.totalPages ?? 1),
  );
  const paginatedUsers = sortedAndFilteredUsers;

  useEffect(() => {
    if (!activeMenuId) {
      return undefined;
    }

    const closeMenu = () => {
      setActiveMenuId(null);
      setActiveMenuPosition(null);
    };

    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [activeMenuId]);

  const openDeleteModal = (user: UserRow) => {
    setDeleteTargetUser(user);
    setReassignTargetUserId("");
    setDeleteErrorMessage("");
    setActiveMenuId(null);
    setActiveMenuPosition(null);
  };

  const closeDeleteModal = () => {
    if (deleteStaffUserState.isLoading) {
      return;
    }
    setDeleteTargetUser(null);
    setReassignTargetUserId("");
    setDeleteErrorMessage("");
  };

  const resetInviteDeliveryState = () => {
    setInviteRetryTarget(null);
    setInviteDeliveryMessage("");
  };

  const openInviteModal = () => {
    resetInviteDeliveryState();
    setIsInviteModalOpen(true);
  };

  const closeInviteModal = () => {
    if (inviteSubmitInFlightRef.current || inviteRetryInFlightRef.current) {
      return;
    }
    setIsInviteModalOpen(false);
    if (inviteRetryTarget) {
      setInviteForm(createDefaultInviteForm());
    }
    resetInviteDeliveryState();
  };

  const handleDeleteUser = () => {
    if (!deleteTargetUser) {
      return;
    }
    if (showDeleteReassignmentField && !reassignTargetUserId) {
      setDeleteErrorMessage(
        "Select another staff member or choose delete without assigning before deleting this user.",
      );
      return;
    }

    const deleteWithoutAssigning =
      reassignTargetUserId === DELETE_WITHOUT_ASSIGNING_VALUE;

    void (async () => {
      try {
        await deleteStaffUser({
          userId: deleteTargetUser.id,
          reassignToUserId:
            showDeleteReassignmentField && !deleteWithoutAssigning
              ? reassignTargetUserId
              : undefined,
          deleteWithoutAssigning,
        }).unwrap();
        const deletedName = deleteTargetUser.name;
        setDeleteTargetUser(null);
        setReassignTargetUserId("");
        setDeleteErrorMessage("");
        showToast(
          deleteWithoutAssigning
            ? `${deletedName} was deleted and assigned work was left unassigned.`
            : `${deletedName} was deleted from the staff directory.`,
          "success",
        );
      } catch (error: unknown) {
        setDeleteErrorMessage(mapDeleteStaffUserErrorMessage(error));
      }
    })();
  };

  const handleInvite = () => {
    if (inviteSubmitInFlightRef.current || inviteRetryTarget) {
      return;
    }
    inviteSubmitInFlightRef.current = true;
    setIsInviteSubmitting(true);
    setInviteDeliveryMessage("");
    const split = splitName(inviteForm.name);
    void (async () => {
      try {
        const result = await inviteStaffUser({
          email: inviteForm.email,
          firstName: split.firstName || "Staff",
          lastName: split.lastName || "",
          role: ROLE_TO_CANONICAL[inviteForm.role],
        }).unwrap();
        if (result.emailQueued === false) {
          setInviteRetryTarget({
            userId: result.userId,
          });
          setInviteDeliveryMessage(STAFF_INVITE_EMAIL_NOT_QUEUED_MESSAGE);
          showToast(STAFF_INVITE_EMAIL_NOT_QUEUED_MESSAGE, "error");
          return;
        }
        setIsInviteModalOpen(false);
        setInviteForm(createDefaultInviteForm());
        resetInviteDeliveryState();
        showToast("Invitation email sent.", "success");
      } catch (error: unknown) {
        showToast(mapPersistErrorMessage(error, "staff"), "error");
      } finally {
        inviteSubmitInFlightRef.current = false;
        setIsInviteSubmitting(false);
      }
    })();
  };

  const handleRetryInviteEmail = () => {
    if (!inviteRetryTarget || inviteRetryInFlightRef.current) {
      return;
    }
    inviteRetryInFlightRef.current = true;
    setIsInviteRetrySubmitting(true);
    setInviteDeliveryMessage("");
    void (async () => {
      try {
        const result = await resendStaffInvite({
          userId: inviteRetryTarget.userId,
        }).unwrap();
        if (result.emailQueued === false) {
          setInviteDeliveryMessage(
            STAFF_INVITE_RESEND_EMAIL_NOT_QUEUED_MESSAGE,
          );
          showToast(STAFF_INVITE_RESEND_EMAIL_NOT_QUEUED_MESSAGE, "error");
          return;
        }
        setIsInviteModalOpen(false);
        setInviteForm(createDefaultInviteForm());
        resetInviteDeliveryState();
        showToast("Invitation email sent.", "success");
      } catch (error: unknown) {
        const message = mapPersistErrorMessage(error, "staff");
        setInviteDeliveryMessage(message);
        showToast(message, "error");
      } finally {
        inviteRetryInFlightRef.current = false;
        setIsInviteRetrySubmitting(false);
      }
    })();
  };

  const suspendUser = (id: string) => {
    void (async () => {
      try {
        await deactivateStaffUser({ userId: id }).unwrap();
      } catch (error: unknown) {
        showToast(mapPersistErrorMessage(error, "staff"), "error");
      }
    })();
  };

  const resumeUser = (id: string) => {
    void (async () => {
      try {
        await reactivateStaffUser({ userId: id }).unwrap();
      } catch (error: unknown) {
        showToast(mapPersistErrorMessage(error, "staff"), "error");
      }
    })();
  };

  const changeUserRole = (id: string, role: StaffRoleLabel) => {
    void (async () => {
      try {
        await updateStaffUserRole({
          userId: id,
          role: ROLE_TO_CANONICAL[role],
        }).unwrap();
      } catch (error: unknown) {
        showToast(mapPersistErrorMessage(error, "staff"), "error");
      }
    })();
  };

  const resendInviteEmail = (user: UserRow) => {
    if (resendingInviteUserId) {
      return;
    }
    setResendingInviteUserId(user.id);
    setActiveMenuId(null);
    setActiveMenuPosition(null);
    void (async () => {
      try {
        const result = await resendStaffInvite({ userId: user.id }).unwrap();
        if (result.emailQueued === false) {
          showToast(STAFF_INVITE_RESEND_EMAIL_NOT_QUEUED_MESSAGE, "error");
          return;
        }
        showToast(`Invitation email sent to ${user.email}.`, "success");
      } catch (error: unknown) {
        showToast(mapPersistErrorMessage(error, "staff"), "error");
      } finally {
        setResendingInviteUserId(null);
      }
    })();
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      <Card>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div>
            <h3
              data-testid="settings-users-staff-directory-heading"
              className="text-lg font-semibold leading-6 text-irongrey"
            >
              Staff Directory
            </h3>
            <p className="text-sm text-text-secondary mt-1">
              Manage system access for school staff.
            </p>
          </div>
          <Button
            data-testid="settings-users-invite-button"
            variant="outline"
            onClick={openInviteModal}
          >
            <Icon name="UserPlus" className="w-4 h-4 mr-2" /> Invite User
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Search" className="text-text-tertiary w-5 h-5" />
            </div>
            <input
              className="block w-full pl-10 sm:text-sm border-border rounded-md focus:ring-primary focus:border-primary h-10 border"
              placeholder="Search staff..."
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="w-full sm:w-48">
            <CustomSelect
              testId="settings-users-role-filter"
              options={[
                { label: "All Roles", value: "all" },
                ...ROLE_OPTIONS.map((role) => ({ label: role, value: role })),
              ]}
              value={roleFilter}
              onChange={setRoleFilter}
            />
          </div>
          <div className="w-full sm:w-44">
            <CustomSelect
              testId="settings-users-status-filter"
              options={[
                { label: "All Statuses", value: "all" },
                { label: "Active", value: "active" },
                { label: "Invited", value: "invited" },
                { label: "Deactivated", value: "deactivated" },
              ]}
              value={statusFilter}
              onChange={(value) =>
                setStatusFilter(value as typeof statusFilter)
              }
            />
          </div>
        </div>

        <div className="overflow-x-auto border border-border rounded-md">
          <table className="table-sticky-last-action-col min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-muted transition-colors select-none"
                  onClick={toggleSort}
                >
                  <div className="flex items-center gap-1">
                    Name
                    <Icon
                      name={
                        sortConfig?.direction === "desc"
                          ? "ArrowDownAZ"
                          : "ArrowUpAZ"
                      }
                      className={`w-3 h-3 ${sortConfig ? "text-primary" : "text-text-tertiary"}`}
                    />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Last Active
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-border">
              {paginatedUsers.length === 0 && users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-14 text-center">
                    <Icon
                      name="Users"
                      className="w-8 h-8 mx-auto text-text-tertiary mb-3"
                    />
                    <p className="text-sm font-medium text-irongrey">
                      No staff yet
                    </p>
                    <p className="text-xs text-text-secondary mt-1">
                      Invite your first staff member to get started.
                    </p>
                  </td>
                </tr>
              )}
              {paginatedUsers.length === 0 && users.length > 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-14 text-center">
                    <Icon
                      name="SearchX"
                      className="w-8 h-8 mx-auto text-text-tertiary mb-3"
                    />
                    <p className="text-sm font-medium text-irongrey">
                      No matching staff
                    </p>
                    <p className="text-xs text-text-secondary mt-1">
                      Try adjusting your search or filters.
                    </p>
                  </td>
                </tr>
              )}
              {paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-irongrey">
                        {user.name}
                      </span>
                      <span className="text-xs text-text-secondary">
                        {user.email}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-40">
                      {isRoleEditor ? (
                        <CustomSelect
                          testId={`settings-users-role-${user.rowId}`}
                          options={ROLE_OPTIONS.map((role) => ({
                            label: role,
                            value: role,
                          }))}
                          value={user.role}
                          onChange={(value) =>
                            changeUserRole(user.id, value as StaffRoleLabel)
                          }
                        />
                      ) : (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${user.badgeColor}`}
                        >
                          {user.role}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${user.status === "Active" ? "bg-success-background text-jungle" : user.status === "Invited" ? "bg-warning-background text-irongrey" : "bg-error-background text-strawberry"}`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {user.active}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative">
                    <button
                      data-testid={`settings-users-row-action-menu-${user.rowId}`}
                      className="text-text-tertiary hover:text-text-secondary"
                      onClick={(event) => {
                        if (activeMenuId === user.rowId) {
                          setActiveMenuId(null);
                          setActiveMenuPosition(null);
                          return;
                        }
                        setActiveMenuPosition(
                          getRowActionMenuPosition(event.currentTarget),
                        );
                        setActiveMenuId(user.rowId);
                      }}
                    >
                      <Icon name="MoreHorizontal" className="w-5 h-5" />
                    </button>
                    {activeMenuId === user.rowId &&
                    activeMenuPosition &&
                    typeof document !== "undefined"
                      ? createPortal(
                          <>
                            <div
                              data-passive-click-surface="true"
                              data-testid={`settings-users-row-action-backdrop-${user.rowId}`}
                              className="fixed inset-0 cursor-default"
                              style={{
                                zIndex: ROW_ACTION_MENU_LAYER_Z_INDEX - 1,
                              }}
                              onClick={() => {
                                setActiveMenuId(null);
                                setActiveMenuPosition(null);
                              }}
                            ></div>
                            <div
                              className="fixed bg-white rounded-md shadow-lg border border-border overflow-hidden py-1 w-32"
                              style={{
                                top: activeMenuPosition.top,
                                left: activeMenuPosition.left,
                                transform: "translateX(-100%)",
                                zIndex: ROW_ACTION_MENU_LAYER_Z_INDEX,
                              }}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {user.status === "Active" ? (
                                <>
                                  <button
                                    data-testid={`settings-users-view-as-${user.rowId}`}
                                    onClick={() => {
                                      onViewAs(user.id);
                                      setActiveMenuId(null);
                                      setActiveMenuPosition(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-irongrey hover:bg-muted"
                                  >
                                    View as…
                                  </button>
                                  <button
                                    data-testid={`settings-users-suspend-action-${user.rowId}`}
                                    onClick={() => {
                                      suspendUser(user.id);
                                      setActiveMenuId(null);
                                      setActiveMenuPosition(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-irongrey hover:bg-muted"
                                  >
                                    Suspend
                                  </button>
                                  <button
                                    data-testid={`settings-users-delete-action-${user.rowId}`}
                                    onClick={() => openDeleteModal(user)}
                                    className="w-full text-left px-4 py-2 text-sm text-strawberry hover:bg-error-background"
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : user.status === "Deactivated" ? (
                                <>
                                  <button
                                    data-testid={`settings-users-reactivate-action-${user.rowId}`}
                                    onClick={() => {
                                      resumeUser(user.id);
                                      setActiveMenuId(null);
                                      setActiveMenuPosition(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-irongrey hover:bg-muted"
                                  >
                                    Reactivate
                                  </button>
                                  <button
                                    data-testid={`settings-users-delete-action-${user.rowId}`}
                                    onClick={() => openDeleteModal(user)}
                                    className="w-full text-left px-4 py-2 text-sm text-strawberry hover:bg-error-background"
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    data-testid={`settings-users-remind-action-${user.rowId}`}
                                    disabled={resendingInviteUserId === user.id}
                                    onClick={() => resendInviteEmail(user)}
                                    className="w-full text-left px-4 py-2 text-sm text-irongrey hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {resendingInviteUserId === user.id
                                      ? "Sending..."
                                      : "Resend invite"}
                                  </button>
                                  <button
                                    data-testid={`settings-users-deactivate-action-${user.rowId}`}
                                    onClick={() => {
                                      suspendUser(user.id);
                                      setActiveMenuId(null);
                                      setActiveMenuPosition(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-irongrey hover:bg-muted"
                                  >
                                    Deactivate
                                  </button>
                                  <button
                                    data-testid={`settings-users-delete-action-${user.rowId}`}
                                    onClick={() => openDeleteModal(user)}
                                    className="w-full text-left px-4 py-2 text-sm text-strawberry hover:bg-error-background"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </>,
                          document.body,
                        )
                      : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalFilteredPages > 1 && (
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalFilteredPages}
            onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() =>
              setCurrentPage((p) => Math.min(totalFilteredPages, p + 1))
            }
            prevTestId="settings-users-page-prev"
            nextTestId="settings-users-page-next"
            className="mt-4"
          />
        )}
      </Card>

      <Modal
        testId="settings-users-invite-modal"
        isOpen={isInviteModalOpen}
        onClose={closeInviteModal}
        title="Invite User"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={inviteForm.name}
            onChange={(event) =>
              setInviteForm({ ...inviteForm, name: event.target.value })
            }
            disabled={Boolean(inviteRetryTarget)}
            placeholder="Full name"
          />
          <Input
            label="Email"
            type="email"
            value={inviteForm.email}
            onChange={(event) =>
              setInviteForm({ ...inviteForm, email: event.target.value })
            }
            disabled={Boolean(inviteRetryTarget)}
            placeholder="email@school.edu"
          />
          {inviteRetryTarget ? (
            <Input
              label="Role"
              value={inviteForm.role}
              disabled
              readOnly
              data-testid="settings-users-invite-role-readonly"
            />
          ) : (
            <CustomSelect
              label="Role"
              options={ROLE_OPTIONS.map((role) => ({
                label: role,
                value: role,
              }))}
              value={inviteForm.role}
              onChange={(value) =>
                setInviteForm({ ...inviteForm, role: value as StaffRoleLabel })
              }
              testId="settings-users-invite-role"
            />
          )}
          {inviteDeliveryMessage ? (
            <div
              data-testid="settings-users-invite-delivery-alert"
              className="rounded-md border border-error-border bg-error-background p-3 text-sm font-medium text-strawberry"
            >
              {inviteDeliveryMessage}
            </div>
          ) : null}
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="ghost"
              onClick={closeInviteModal}
              disabled={isInviteSubmitting || isInviteRetrySubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={
                inviteRetryTarget ? handleRetryInviteEmail : handleInvite
              }
              disabled={isInviteSubmitting || isInviteRetrySubmitting}
            >
              {isInviteSubmitting || isInviteRetrySubmitting
                ? "Sending..."
                : inviteRetryTarget
                  ? "Retry Email"
                  : "Send Invitation"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        testId="settings-users-delete-modal"
        isOpen={Boolean(deleteTargetUser)}
        onClose={closeDeleteModal}
        title="Delete Staff User"
      >
        {deleteTargetUser ? (
          <div className="space-y-4">
            <div className="rounded-md border border-error-border bg-error-background/40 p-4">
              <p className="text-sm font-semibold text-irongrey">
                Delete {deleteTargetUser.name}?
              </p>
              <p className="mt-1 text-sm text-text-secondary">
                This removes the user from the Staff Directory. Assigned
                submissions must be moved to another active staff member first.
              </p>
            </div>

            <div className="rounded-md border border-border bg-muted/40 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                Assigned work check
              </p>
              {deleteDependencyLoading ? (
                <p className="mt-2 text-sm text-text-secondary">
                  Checking assigned work before delete...
                </p>
              ) : (
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded border border-border bg-white p-2">
                    <div className="text-base font-semibold text-irongrey">
                      {deleteAssignedAdmissionsEnrollmentCount}
                    </div>
                    <div className="text-[11px] text-text-secondary">
                      Admissions / Enrollment
                    </div>
                  </div>
                  <div className="rounded border border-border bg-white p-2">
                    <div className="text-base font-semibold text-irongrey">
                      {deleteAssignedFinancialAidCount}
                    </div>
                    <div className="text-[11px] text-text-secondary">
                      Financial Aid
                    </div>
                  </div>
                  <div className="rounded border border-border bg-white p-2">
                    <div className="text-base font-semibold text-irongrey">
                      {deleteAssignedTaskCount}
                    </div>
                    <div className="text-[11px] text-text-secondary">Tasks</div>
                  </div>
                </div>
              )}
              {!deleteDependencyLoading && !deleteRequiresReassignment ? (
                <p className="mt-3 text-sm text-text-secondary">
                  No assigned submissions were found for this user.
                </p>
              ) : null}
              {deleteDependencyError ? (
                <p className="mt-3 text-sm text-strawberry">
                  Unable to complete the assignment check. Select a reassignment
                  user so the server can safely block or complete the delete.
                </p>
              ) : null}
            </div>

            {showDeleteReassignmentField ? (
              <CustomSelect
                testId="settings-users-delete-reassign-to"
                label="Assigned work handling"
                options={reassignmentOptions}
                value={reassignTargetUserId}
                onChange={(value) => {
                  setReassignTargetUserId(value);
                  setDeleteErrorMessage("");
                }}
              />
            ) : null}

            {deleteErrorMessage ? (
              <div className="rounded-md border border-error-border bg-error-background p-3 text-sm font-medium text-strawberry">
                {deleteErrorMessage}
              </div>
            ) : null}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <button
                data-testid="settings-users-confirm-delete"
                type="button"
                disabled={
                  deleteDependencyLoading ||
                  deleteStaffUserState.isLoading ||
                  (showDeleteReassignmentField && !reassignTargetUserId)
                }
                onClick={handleDeleteUser}
                className="inline-flex items-center justify-center rounded-md bg-strawberry px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-strawberry/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleteStaffUserState.isLoading ? "Deleting..." : "Delete User"}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      {SHOW_ADMINISTRATOR_GOVERNANCE ? (
        <AdministratorGovernanceSection
          existingUsers={users.map((user) => ({
            id: user.id,
            name: user.name,
            email: user.email,
          }))}
        />
      ) : null}

      <Card>
        <SectionHeader
          title="Principals & Signers"
          description="Assign division leaders for official correspondence."
        />
        <div className="space-y-4 max-w-4xl">
          {settingsState.divisions.map((division) => (
            <div
              key={division.id}
              className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-border rounded-md bg-white"
            >
              <label className="block text-sm font-medium text-irongrey w-48">
                {division.displayName}
              </label>
              <div className="w-full max-w-lg">
                <CustomSelect
                  testId={`settings-users-principal-${division.id}`}
                  options={buildPrincipalOptionsForDivision({
                    users: users.map((user) => ({
                      id: user.id,
                      name: user.name,
                    })),
                    division,
                  })}
                  value={divisionPrincipal[division.id] || ""}
                  onChange={(value) => {
                    const nextPrincipal = {
                      ...divisionPrincipal,
                      [division.id]: value,
                    };
                    setDivisionPrincipal(nextPrincipal);
                    void (async () => {
                      try {
                        await persistDivisionAssignments(
                          nextPrincipal,
                          divisionSecretaries,
                        );
                      } catch (error: unknown) {
                        showToast(
                          mapPersistErrorMessage(error, "division"),
                          "error",
                        );
                      }
                    })();
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card data-testid="settings-users-notification-recipients-section">
        <SectionHeader
          title="Notification Recipients"
          description="Configure who receives system emails per division."
        />
        <div className="space-y-4 max-w-4xl">
          {settingsState.divisions.map((division) => (
            <div
              key={division.id}
              className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-border rounded-md bg-white"
            >
              <div className="w-full md:w-1/3">
                <label className="block text-sm font-medium text-irongrey">
                  {division.displayName} Secretary
                </label>
                <p className="text-xs text-text-secondary mt-0.5">
                  Receives copies of all acceptances.
                </p>
              </div>
              <div className="w-full md:w-2/3 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Mail" className="text-text-tertiary w-4 h-4" />
                </div>
                <input
                  className="block w-full pl-10 sm:text-sm border-border rounded-md focus:ring-primary focus:border-primary h-10 border"
                  type="email"
                  value={divisionSecretaries[division.id] || ""}
                  onChange={(event) =>
                    setDivisionSecretaries((previous) => ({
                      ...previous,
                      [division.id]: event.target.value,
                    }))
                  }
                  onBlur={() => {
                    void (async () => {
                      try {
                        await persistDivisionAssignments(
                          divisionPrincipal,
                          divisionSecretaries,
                        );
                      } catch (error: unknown) {
                        showToast(
                          mapPersistErrorMessage(error, "division"),
                          "error",
                        );
                      }
                    })();
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card data-testid="settings-users-permissions-matrix-section">
        <SectionHeader
          title="Permissions Matrix"
          description="Configure access levels for each role."
        />
        <div className="overflow-x-auto border border-border rounded-md">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-irongrey uppercase tracking-wider w-1/3">
                  Permission
                </th>
                {ROLE_OPTIONS.map((role) => (
                  <th
                    key={role}
                    className="px-2 py-4 text-center text-xs font-semibold text-text-secondary"
                  >
                    {role}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-border">
              {Object.keys(permissions).map((permission, index) => {
                const sectionTitle = PERMISSION_SECTION_STARTS[index];

                return (
                  <React.Fragment key={permission}>
                    {sectionTitle != null && (
                      <tr className="bg-muted/50">
                        <td
                          className="px-6 py-3 text-xs font-bold text-text-secondary uppercase tracking-wider"
                          colSpan={ROLE_OPTIONS.length + 1}
                        >
                          {sectionTitle}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td className="px-6 py-3 text-sm text-irongrey">
                        {permission}
                      </td>
                      {ROLE_OPTIONS.map((role) => (
                        <td key={role} className="px-2 py-3 text-center">
                          <div className="flex justify-center">
                            {role === "Super Administrator" ? (
                              // Super Administrator always has all permissions — non-interactive indicator.
                              <Icon
                                name="CheckCircle2"
                                className="text-aquamarine w-5 h-5"
                                aria-label="Always granted"
                              />
                            ) : (
                              <Checkbox
                                checked={
                                  permissions[permission]?.[role] || false
                                }
                                onChange={() =>
                                  togglePermission(permission, role)
                                }
                              />
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Phase 1: Staff Signup Codes */}
      <StaffCodesSection />

      {/* Phase 1: Staff self-signup approvals */}
      <PendingStaffRequestsSection />

      {/* Phase 1: Approved Email Domains */}
      <ApprovedDomainsSection />

      <Toast
        message={toastMessage}
        variant={toastVariant}
        visible={toastVisible}
        onDismiss={() => setToastVisible(false)}
      />
    </div>
  );
}
